<script setup>
import { ref, watch, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import SectionLayout from './SectionLayout.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import NextInput from 'next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import PaymentSettingsAPI from 'dashboard/api/paymentSettings';

const isLoading = ref(false);
const isSubmitting = ref(false);

// Form state
const pixKey = ref('');
const pixKeyType = ref('cpf');
const cardEnabled = ref(true);
const maxInstallments = ref(12);
const debitEnabled = ref(true);
const debitFee = ref(0);
const cashEnabled = ref(true);
const cardFees = ref({});

const pixKeyTypes = [
  { value: 'cpf', label: 'CPF' },
  { value: 'cnpj', label: 'CNPJ' },
  { value: 'email', label: 'E-mail' },
  { value: 'phone', label: 'Telefone' },
  { value: 'random', label: 'Chave Aleatoria' },
];

const defaultCardFees = {
  1: 0,
  2: 2.99,
  3: 3.99,
  4: 4.99,
  5: 5.99,
  6: 6.99,
  7: 7.99,
  8: 8.99,
  9: 9.99,
  10: 10.99,
  11: 11.99,
  12: 12.99,
};

const loadSettings = async () => {
  isLoading.value = true;
  try {
    const response = await PaymentSettingsAPI.get();
    const data = response.data;

    pixKey.value = data.pix_key || '';
    pixKeyType.value = data.pix_key_type || 'cpf';
    cardEnabled.value = data.card_enabled ?? true;
    maxInstallments.value = data.max_installments || 12;
    debitEnabled.value = data.debit_enabled ?? true;
    debitFee.value = data.debit_fee || 0;
    cashEnabled.value = data.cash_enabled ?? true;
    cardFees.value = data.card_fees || defaultCardFees;

    // Ensure all installments have a fee value
    for (let i = 1; i <= 24; i++) {
      if (cardFees.value[String(i)] === undefined) {
        cardFees.value[String(i)] = defaultCardFees[String(i)] || 0;
      }
    }
  } catch (error) {
    console.error('Error loading payment settings:', error);
    // Initialize with defaults
    cardFees.value = { ...defaultCardFees };
  } finally {
    isLoading.value = false;
  }
};

const handleSubmit = async () => {
  isSubmitting.value = true;
  try {
    // Filter card fees to only include enabled installments
    const filteredFees = {};
    for (let i = 1; i <= maxInstallments.value; i++) {
      filteredFees[String(i)] = Number(cardFees.value[String(i)] || 0);
    }

    await PaymentSettingsAPI.update({
      pix_key: pixKey.value,
      pix_key_type: pixKeyType.value,
      card_enabled: cardEnabled.value,
      max_installments: maxInstallments.value,
      debit_enabled: debitEnabled.value,
      debit_fee: Number(debitFee.value),
      cash_enabled: cashEnabled.value,
      card_fees: filteredFees,
    });
    useAlert('Configuracoes de pagamento salvas com sucesso!');
  } catch (error) {
    console.error('Error saving payment settings:', error);
    useAlert('Erro ao salvar configuracoes de pagamento');
  } finally {
    isSubmitting.value = false;
  }
};

const useDefaultFees = () => {
  cardFees.value = { ...defaultCardFees };
};

onMounted(() => {
  loadSettings();
});
</script>

<template>
  <SectionLayout
    title="Configuracoes de Pagamento"
    description="Configure as formas de pagamento disponíveis para fechamento de vendas, taxas de cartao e chave PIX."
    with-border
  >
    <div v-if="isLoading" class="flex justify-center py-8">
      <span class="i-lucide-loader-2 animate-spin text-2xl text-n-slate-10" />
    </div>

    <form v-else class="grid gap-6" @submit.prevent="handleSubmit">
      <!-- PIX Settings -->
      <div class="space-y-4">
        <h4
          class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
        >
          <span class="i-lucide-qr-code text-blue-500" />
          Configuracoes do PIX
        </h4>

        <div class="grid gap-4 pl-6">
          <WithLabel label="Tipo de Chave PIX">
            <select
              v-model="pixKeyType"
              class="w-full rounded-lg border border-n-weak bg-n-solid-1 dark:bg-slate-800 px-3 py-2.5 text-sm text-n-slate-12"
            >
              <option
                v-for="type in pixKeyTypes"
                :key="type.value"
                :value="type.value"
                class="bg-white dark:bg-slate-800"
              >
                {{ type.label }}
              </option>
            </select>
          </WithLabel>

          <WithLabel
            label="Chave PIX"
            help-message="Informe sua chave PIX para exibir no fechamento de vendas"
          >
            <NextInput
              v-model="pixKey"
              type="text"
              class="w-full"
              placeholder="Digite sua chave PIX"
            />
          </WithLabel>
        </div>
      </div>

      <!-- Card Settings -->
      <div class="space-y-4">
        <h4
          class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
        >
          <span class="i-lucide-credit-card text-purple-500" />
          Configuracoes do Cartao de Credito
        </h4>

        <div class="grid gap-4 pl-6">
          <div class="flex items-center gap-3">
            <Checkbox v-model="cardEnabled" />
            <span class="text-sm text-n-slate-12">Aceitar pagamento com cartao de credito</span>
          </div>

          <WithLabel
            v-if="cardEnabled"
            label="Numero Maximo de Parcelas"
            help-message="Quantidade maxima de parcelas permitidas"
          >
            <select
              v-model="maxInstallments"
              class="w-full rounded-lg border border-n-weak bg-n-solid-1 dark:bg-slate-800 px-3 py-2.5 text-sm text-n-slate-12"
            >
              <option
                v-for="n in 24"
                :key="n"
                :value="n"
                class="bg-white dark:bg-slate-800"
              >
                {{ n }}x
              </option>
            </select>
          </WithLabel>

          <div v-if="cardEnabled" class="space-y-3">
            <div class="flex items-center justify-between">
              <p class="text-sm font-medium text-n-slate-11">
                Taxa por Parcela (%)
              </p>
              <NextButton
                type="button"
                faded
                slate
                xs
                label="Usar taxas padrao"
                icon="i-lucide-refresh-cw"
                @click="useDefaultFees"
              />
            </div>

            <div
              class="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-64 overflow-auto p-2 bg-n-alpha-1 rounded-lg"
            >
              <div
                v-for="n in maxInstallments"
                :key="n"
                class="flex flex-col gap-1"
              >
                <label class="text-xs text-n-slate-10">{{ n }}x</label>
                <input
                  v-model.number="cardFees[String(n)]"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  class="w-full rounded border border-n-weak bg-white dark:bg-slate-800 px-2 py-1.5 text-sm text-n-slate-12"
                  :placeholder="n === 1 ? '0' : ''"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Debit Card Settings -->
      <div class="space-y-4">
        <h4
          class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
        >
          <span class="i-lucide-credit-card text-blue-500" />
          Configuracoes do Cartao de Debito
        </h4>

        <div class="grid gap-4 pl-6">
          <div class="flex items-center gap-3">
            <Checkbox v-model="debitEnabled" />
            <span class="text-sm text-n-slate-12">Aceitar pagamento com cartao de debito</span>
          </div>

          <WithLabel
            v-if="debitEnabled"
            label="Taxa do Debito (%)"
            help-message="Taxa aplicada em pagamentos no debito"
          >
            <NextInput
              v-model.number="debitFee"
              type="number"
              step="0.01"
              min="0"
              max="100"
              class="w-32"
              placeholder="0.00"
            />
          </WithLabel>
        </div>
      </div>

      <!-- Cash Settings -->
      <div class="space-y-4">
        <h4
          class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
        >
          <span class="i-lucide-banknote text-green-500" />
          Configuracoes de Dinheiro
        </h4>

        <div class="grid gap-4 pl-6">
          <div class="flex items-center gap-3">
            <Checkbox v-model="cashEnabled" />
            <span class="text-sm text-n-slate-12">Aceitar pagamento em dinheiro</span>
          </div>
        </div>
      </div>

      <!-- Submit Button -->
      <div class="flex gap-2 pt-4 border-t border-n-weak">
        <NextButton
          blue
          type="submit"
          :is-loading="isSubmitting"
          label="Salvar Configuracoes"
          icon="i-lucide-save"
        />
      </div>
    </form>
  </SectionLayout>
</template>

<style scoped>
select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem;
}

select option {
  background-color: var(--n-solid-1);
  color: var(--n-slate-12);
}

:deep(.dark) select option,
.dark select option {
  background-color: #1e293b;
  color: #f1f5f9;
}

@media (prefers-color-scheme: dark) {
  select option {
    background-color: #1e293b;
    color: #f1f5f9;
  }
}
</style>
